import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form as FormikForm, useFormikContext } from 'formik';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { LuSave, LuPlus, LuFileEdit, LuX, LuCheck } from 'react-icons/lu';
import { isEmpty, sortBy } from 'lodash';

import { FormSelectField, Button, TableLookup, FormTextField } from '_components/_core';
import { SPECIAL_COLUMNS } from '_components/_core/Table';

import ValidationSchema from './utilities/ValidationSchema';

import {
  CustomCard,
  CustomCardBody,
  CustomCardFooter,
  CustomCardHeader,
} from '../../../../styles';

const tableConfig = {
  defaultFilters: {},
  defaultSorting: { field: 'payment_gateway_name', order: 'asc' },
  defaultPagination: { currentPage: 1, itemsPerPage: 10 },
};

// Inline Edit Cell Component
const InlineEditCell = ({ row, fieldName, options, onSave, isDisabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);

    // Initialize field value when starting to edit
    if (!isEditing && fieldName === 'charge_status_id') {
      setFieldValue(fieldName, row.charge_status_id);
    } else if (!isEditing && fieldName === 'payment_gateway_id') {
      setFieldValue(fieldName, row.payment_gateway_id);
    } else if (!isEditing && fieldName === 'code') {
      setFieldValue(fieldName, row.code);
    }
  };

  const handleSave = () => {
    if (!errors[fieldName]) {
      // Ensure we send all three properties with the update
      onSave(row.id, {
        payment_gateway_id:
          fieldName === 'payment_gateway_id' ? values[fieldName] : row.payment_gateway_id,
        charge_status_id:
          fieldName === 'charge_status_id' ? values[fieldName] : row.charge_status_id,
        code: fieldName === 'code' ? values[fieldName] : row.code,
      });
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="d-flex align-items-center">
        {fieldName === 'code' ? (
          <FormTextField
            name={fieldName}
            placeholder="Código"
            className={`form-control ${errors[fieldName] && touched[fieldName] ? 'is-invalid' : ''}`}
          />
        ) : (
          <FormSelectField
            name={fieldName}
            options={options}
            placeholder="Selecione"
            isSearchable={true}
            className="flex-grow-1"
          />
        )}
        <div className="ml-2 d-flex align-items-center">
          <Button
            variant="white"
            className="d-flex align-items-center justify-content-center"
            size="sm"
            onClick={handleSave}
            disabled={isDisabled}
          >
            <LuCheck size={16} />
          </Button>
          <Button
            variant="white"
            className="d-flex align-items-center justify-content-center ml-1"
            size="sm"
            onClick={handleToggleEdit}
          >
            <LuX size={16} />
          </Button>
        </div>
      </div>
    );
  }

  const getDisplayValue = () => {
    if (fieldName === 'charge_status_id') {
      const option = options.find(opt => opt.value === row.charge_status_id);
      return option?.label || 'Não definido';
    }
    if (fieldName === 'payment_gateway_id') {
      const option = options.find(opt => opt.value === row.payment_gateway_id);
      return option?.label || 'Não definido';
    }
    if (fieldName === 'code') {
      return row.code || 'Não definido';
    }
    return 'Desconhecido';
  };

  return (
    <div className="d-flex justify-content-between align-items-center">
      <span>{getDisplayValue()}</span>
      <Button variant="link" size="sm" onClick={handleToggleEdit} disabled={isDisabled}>
        <LuFileEdit />
      </Button>
    </div>
  );
};

const ChargeStatusPaymentGateway = ({
  onCreateChargeStatusGateway,
  onUpdateChargeStatusGateway,
  onDeleteChargeStatusGateway,
  onFetchChargeStatusGateway,
  onFetchChargeStatus,
  onFetchPaymentGateways,
  chargeStatusPaymentGateways,
  chargeStatus,
  paymentGateways,
  isLoading,
}) => {
  const [isFormEditing, setIsFormEditing] = useState(false);
  const [currentAssociation, setCurrentAssociation] = useState(null);
  const [filteredMethodsGateways, setFilteredMethodsGateways] = useState([]);

  useEffect(() => {
    onFetchChargeStatusGateway();
    onFetchChargeStatus();
    onFetchPaymentGateways();
  }, [onFetchChargeStatusGateway, onFetchChargeStatus, onFetchPaymentGateways]);

  const handleCreateAssociation = useCallback(
    (values, { resetForm }) => {
      // Ensure all required fields are included in the payload
      const payload = {
        payment_gateway_id: values.payment_gateway_id,
        charge_status_id: values.charge_status_id,
        code: values.code,
      };

      if (isFormEditing && currentAssociation) {
        onUpdateChargeStatusGateway(currentAssociation.id, payload, () => {
          setIsFormEditing(false);
          setCurrentAssociation(null);
          onFetchChargeStatusGateway();
        });
      } else {
        onCreateChargeStatusGateway(payload, () => {
          onFetchChargeStatusGateway();
        });
      }
      resetForm();
    },
    [
      isFormEditing,
      currentAssociation,
      onUpdateChargeStatusGateway,
      onCreateChargeStatusGateway,
      onFetchChargeStatusGateway,
    ],
  );

  const handleEditAssociation = useCallback(
    association => {
      const selectedAssociation = chargeStatusPaymentGateways.find(
        methodGateway => methodGateway.id === association,
      );

      setCurrentAssociation({
        ...selectedAssociation,
      });
      setIsFormEditing(true);
    },
    [chargeStatusPaymentGateways],
  );

  const handleCancelEdit = useCallback(resetForm => {
    setIsFormEditing(false);
    setCurrentAssociation(null);
    resetForm();
  }, []);

  const handleConfirmDelete = useCallback(
    association => {
      onDeleteChargeStatusGateway(association, () => {
        onFetchChargeStatusGateway();
      });
    },
    [onDeleteChargeStatusGateway, onFetchChargeStatusGateway],
  );

  const handleInlineUpdate = useCallback(
    (id, values) => {
      // Ensure we have all required fields in the update
      onUpdateChargeStatusGateway(id, values, () => {
        onFetchChargeStatusGateway();
      });
    },
    [onUpdateChargeStatusGateway, onFetchChargeStatusGateway],
  );

  useEffect(() => {
    if (
      chargeStatusPaymentGateways?.length &&
      chargeStatus?.length &&
      paymentGateways?.length
    ) {
      const formattedData = sortBy(chargeStatusPaymentGateways, 'sort_order').map(
        association => ({
          ...association,
          charge_status_name: getChargeStatusNameById(association.charge_status_id),
          payment_gateway_name: getGatewayNameById(association.payment_gateway_id),
        }),
      );
      setFilteredMethodsGateways(formattedData);
    }
  }, [chargeStatusPaymentGateways, chargeStatus, paymentGateways]);

  // Format options for select fields
  const chargeStatusOptions =
    chargeStatus?.map(status => ({
      value: status.id,
      label: status.description,
    })) || [];

  const paymentGatewayOptions =
    sortBy(paymentGateways, 'sort_order')?.map(gateway => ({
      value: gateway.id,
      label: gateway.name,
    })) || [];

  // Helper function to get method or gateway name by id
  const getChargeStatusNameById = useCallback(
    id => {
      if (isEmpty(chargeStatus)) return 'Desconhecido';

      const status = chargeStatus.find(s => s.id === id);

      if (!status) return 'Desconhecido';

      return status.description;
    },
    [chargeStatus],
  );

  const getGatewayNameById = useCallback(
    id => {
      if (isEmpty(paymentGateways)) return 'Desconhecido';

      const gateway = paymentGateways.find(g => g.id === id);

      if (!gateway) return 'Desconhecido';

      return gateway.name;
    },
    [paymentGateways],
  );

  const columns = useMemo(
    () => [
      {
        label: 'Gateway de Pagamento',
        keyName: 'payment_gateway_name',
        formatter: (_, row) => (
          <InlineEditCell
            row={row}
            fieldName="payment_gateway_id"
            options={paymentGatewayOptions}
            onSave={handleInlineUpdate}
            isDisabled={isLoading}
          />
        ),
        width: '30%',
      },
      {
        label: 'Status',
        keyName: 'charge_status_name',
        formatter: (_, row) => (
          <InlineEditCell
            row={row}
            fieldName="charge_status_id"
            options={chargeStatusOptions}
            onSave={handleInlineUpdate}
            isDisabled={isLoading}
          />
        ),
        width: '30%',
      },
      {
        label: 'Código',
        keyName: 'code',
        formatter: (_, row) => (
          <InlineEditCell
            row={row}
            fieldName="code"
            options={[]}
            onSave={handleInlineUpdate}
            isDisabled={isLoading}
          />
        ),
        width: '10%',
      },
      {
        label: 'Ações',
        keyName: 'id',
        specialType: SPECIAL_COLUMNS.ACTIONS,
        align: 'center',
        width: '10%',
      },
    ],
    [paymentGatewayOptions, chargeStatusOptions, handleInlineUpdate, isLoading],
  );

  if (isEmpty(chargeStatus) || isEmpty(paymentGateways)) {
    return <div>Carregando...</div>;
  }

  return (
    <>
      <Formik
        initialValues={{
          charge_status_id: currentAssociation?.charge_status_id || '',
          payment_gateway_id: currentAssociation?.payment_gateway_id || '',
          code: currentAssociation?.code || '',
        }}
        enableReinitialize
        validationSchema={ValidationSchema}
        onSubmit={handleCreateAssociation}
      >
        {({ handleSubmit, resetForm }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col>
                <CustomCard noBorders>
                  <CustomCardHeader>
                    <h3>
                      {isFormEditing ? 'Editar Associação' : 'Adicionar Associação'}
                    </h3>
                  </CustomCardHeader>
                  <CustomCardBody>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">
                            Gateway de Pagamento
                          </Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormSelectField
                            name="payment_gateway_id"
                            options={paymentGatewayOptions}
                            placeholder="Selecione um gateway de pagamento"
                            isSearchable={true}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">Status</Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormSelectField
                            name="charge_status_id"
                            options={chargeStatusOptions}
                            placeholder="Selecione um status"
                            isSearchable={true}
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      <Col md={3}>
                        <Form.Group className="m-0 d-flex align-items-center">
                          <Form.Label className="m-0 mt-2">Código</Form.Label>
                        </Form.Group>
                      </Col>
                      <Col md={9}>
                        <Form.Group>
                          <FormTextField
                            name="code"
                            placeholder="Código usado pelo provedor (ex: PENDING, OVERDUE)"
                            className="form-control"
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                  </CustomCardBody>
                  <CustomCardFooter className="d-flex justify-content-center align-items-center">
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      isLoading={isLoading}
                      disabled={isLoading}
                      className="d-flex align-items-center"
                    >
                      {isFormEditing ? 'Atualizar' : 'Adicionar'}
                      {isFormEditing ? (
                        <LuSave className="ml-2" />
                      ) : (
                        <LuPlus className="ml-2" />
                      )}
                    </Button>
                    <Button
                      variant="link"
                      type="button"
                      size="sm"
                      disabled={isLoading}
                      onClick={() => handleCancelEdit(resetForm)}
                    >
                      Cancelar
                    </Button>
                  </CustomCardFooter>
                </CustomCard>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <Row className="mt-4">
        <Col>
          <Formik
            initialValues={{
              charge_status_id: '',
              payment_gateway_id: '',
              code: '',
            }}
            validationSchema={ValidationSchema}
          >
            <FormikForm>
              <TableLookup
                keyName="id"
                name="payment-gateways-lookup-table"
                columns={columns}
                data={chargeStatusPaymentGateways}
                isLoading={isLoading}
                onEdit={handleEditAssociation}
                onDelete={handleConfirmDelete}
                {...tableConfig}
              />
            </FormikForm>
          </Formik>
        </Col>
      </Row>
    </>
  );
};

ChargeStatusPaymentGateway.defaultProps = {
  chargeStatusPaymentGateways: [],
  chargeStatus: [],
  paymentGateways: [],
  isLoading: false,
};

ChargeStatusPaymentGateway.propTypes = {
  onCreateChargeStatusGateway: PropTypes.func.isRequired,
  onUpdateChargeStatusGateway: PropTypes.func.isRequired,
  onDeleteChargeStatusGateway: PropTypes.func.isRequired,
  onFetchChargeStatusGateway: PropTypes.func.isRequired,
  onFetchChargeStatus: PropTypes.func.isRequired,
  onFetchPaymentGateways: PropTypes.func.isRequired,
  chargeStatusPaymentGateways: PropTypes.array,
  chargeStatus: PropTypes.array,
  paymentGateways: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default ChargeStatusPaymentGateway;
